/* Prime Phones — bottom bar for phones (sections/pp-bottom-bar.liquid).
   - Leaves on a scroll down and comes back on a scroll up, on every page: one passive scroll listener,
     throttled to one check per frame, with an anchor and a 12px threshold (the header's method).
     Always shown within 8px of the top, near the end of the page, on pages too short to scroll and
     while keyboard focus is inside it. On a product page the Add to cart bar that takes its place
     stays (owner, 22 Oct 2026).
   - Like an app (owner, 23 Oct 2026): with "App-like page changes" on, Home, Shop, Pay later and Cart
     swap in place without a reload (assets/pp-app-nav.js); each tab but the cart remembers its address
     (filters) and scroll for 30 minutes; tapping the tab you are on scrolls to the top. With the
     setting off (or when a page cannot swap) the page loads the ordinary way: the lens glides at once,
     the page eases aside, and the next page glides in (snippets/pp-view-transitions.liquid).
   - The lens is always on a tab: on pages that are not one of the bar's (About, a blog post) it stays
     on the tab you came from (Home at first).
   - The lens moves on a lightly damped spring, sleeps when settled, lifts under a finger, and follows
     a finger held and slid along the bar (from anywhere on it); letting go opens the tab under it.
   - Survives the theme editor re-rendering the section (destroy + init). */
(function () {
  'use strict';

  var still = window.matchMedia('(prefers-reduced-motion: reduce)');
  var phone = window.matchMedia('(max-width: 989.98px)');
  var THRESHOLD = 12;
  var HOLD = 220;            /* ms a finger rests on a tab before the lens comes to it */
  var MEMORY = 1800000;      /* a tab remembers its place for 30 minutes */

  function store(k, v) { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  function recall(k) { try { return JSON.parse(sessionStorage.getItem(k) || 'null'); } catch (e) { return null; } }

  function Bar(root) {
    this.root = root;
    this.nav = root.querySelector('.ppbb__nav');
    this.ind = root.querySelector('.ppbb__ind');
    this.items = Array.prototype.slice.call(root.querySelectorAll('.ppbb__item'));
    this.dests = this.items.filter(function (i) { return i.hasAttribute('data-ppbb-dest'); });
    this.app = root.getAttribute('data-app') === 'true' && !!window.PPAppNav;
    /* the pill's movement (setting "Pill movement"): steady glide (calm, the default), springy (iOS give and
       stretch) or still (it appears at the tab tapped, as on Android: nothing travels) */
    this.motion = root.getAttribute('data-bounce') || 'calm';
    this.calm = this.motion !== 'springy';
    this.spr = { x: 0, v: 0, target: 0, w: 64, raf: 0, last: 0, lifted: false, dragging: false, dragEnd: 0 };
    this.off = [];
    this.endSeen = false;
    this.lastY = Math.max(0, window.scrollY);
    this.anchorY = this.lastY;
    this.dir = 0;
    this.down = false;
    this.mark();
    this.init();
  }

  Bar.prototype.on = function (el, type, fn, opts) {
    el.addEventListener(type, fn, opts);
    this.off.push(function () { el.removeEventListener(type, fn, opts); });
  };

  /* the tab of this page (aria-current), or the tab you came from */
  Bar.prototype.mark = function () {
    var self = this;
    this.current = this.dests.filter(function (i) { return i.getAttribute('aria-current') === 'page'; })[0] || null;
    if (this.current) {
      this.tab = this.current;
      store('pp-tab', this.items.indexOf(this.current));
    } else {
      var n = recall('pp-tab');
      this.tab = this.items[n] && this.items[n].hasAttribute('data-ppbb-dest') ? this.items[n] : this.dests[0] || null;
    }
    this.dests.forEach(function (i) { i.classList.toggle('is-tab', !self.current && i === self.tab); });
  };

  Bar.prototype.init = function () {
    var self = this;
    document.body.classList.add('pp-has-bar');

    /* ---------- leave on a scroll down, come back on a scroll up ---------- */
    var ticking = false;
    this.on(window, 'scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; self.onScroll(); });
    }, { passive: true });
    var end = document.querySelector('footer') || this.root.closest('.shopify-section');
    if (end && 'IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (en) { self.endSeen = en[0].isIntersecting; self.update(); });
      this.io.observe(end);
    }
    this.on(this.root, 'focusin', function () { self.update(); });
    this.on(this.root, 'focusout', function () { setTimeout(function () { self.update(); }, 0); });
    this.on(window, 'resize', function () { self.place(true); self.update(); });

    /* ---------- the lens (on an arrival from a bar tap it carries on from the item tapped) ---------- */
    var arrive = window.__ppArrive;
    window.__ppArrive = null;
    if (arrive && this.current && this.items[arrive.from] && !still.matches) {
      var from = this.target(this.items[arrive.from]);
      this.spr.x = from.x; this.spr.w = from.w; this.spr.v = 0;
      this.ind.classList.add('is-on');
      this.draw();
      requestAnimationFrame(function () { self.place(false); });
    } else {
      this.place(true);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self.place(true); });
    this.on(this.nav, 'pointerdown', function (e) { self.press(e); });
    this.on(this.nav, 'pointermove', function (e) { self.move(e); });
    /* a finger is tied to the tab it first touched; the bar takes it over for a slide, and that tab's
       'lost capture' is not the finger lifting: only the bar's own counts */
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(function (t) {
      self.on(self.nav, t, function (e) { if (t === 'lostpointercapture' && e.target !== self.nav) return; self.up(e); });
    });

    /* ---------- items ---------- */
    this.on(this.nav, 'click', function (e) { self.click(e); });
    this.on(this.nav, 'keydown', function (e) { self.keys(e); });

    /* ---------- app-like page changes: name the pages that swap in place ---------- */
    if (this.app) {
      window.PPAppNav.pages(this.dests.map(function (it) {
        return { href: self.placeOf(it).u, i: self.items.indexOf(it), cart: self.isCart(it) };
      }));
      /* after a swap or a back/forward: the tab follows the address */
      this.on(document, 'pp:page', function () { self.sync(); });
    }

    /* ---------- product page: the handle on the Add to cart bar brings the menu back, until the real
       Add to cart button is on screen again ---------- */
    var handle = this.root.querySelector('[data-ppbb-handle]');
    if (handle) {
      this.on(handle, 'click', function () {
        document.body.classList.add('pp-atc-dismissed');
        var to = self.current || self.tab || self.items[0];
        if (to) to.focus({ preventScroll: true });
      });
    }
    this.watchAtc();

    /* ---------- cart count ---------- */
    this.subscribeCart();

    this.update();
  };

  Bar.prototype.watchAtc = function () {
    if (this.atcIo) this.atcIo.disconnect();
    var marker = document.querySelector('[data-pp-atc-end]') || document.querySelector('.ppc');
    if (marker && 'IntersectionObserver' in window) {
      this.atcIo = new IntersectionObserver(function (en) {
        if (en[en.length - 1].isIntersecting) document.body.classList.remove('pp-atc-dismissed');
      });
      this.atcIo.observe(marker);
    }
  };

  Bar.prototype.destroy = function () {
    this.off.forEach(function (f) { f(); });
    if (this.io) this.io.disconnect();
    if (this.atcIo) this.atcIo.disconnect();
    if (this.spr.raf) cancelAnimationFrame(this.spr.raf);
    clearTimeout(this.holdTimer);
    clearTimeout(this.keepTimer);
    (this.unsub || []).forEach(function (u) { try { u(); } catch (e) {} });
    document.body.classList.remove('pp-has-bar', 'pp-nav-hidden', 'pp-nav-small', 'pp-atc-dismissed');
  };

  Bar.prototype.isCart = function (it) {
    return it.pathname.replace(/[/]+$/, '').split('/').pop() === 'cart';
  };

  /* A drawer, the search, the chat or a popup is open: leave the bar where it is. */
  Bar.prototype.busy = function () {
    return /\b(overflow-hidden|pp-locked)\b/.test(document.body.className) || !!document.querySelector('header-drawer details[open]');
  };

  Bar.prototype.onScroll = function () {
    var y = Math.max(0, window.scrollY);
    if (this.busy()) { this.lastY = y; return; }
    if (y <= 8) {
      this.dir = 0; this.anchorY = y; this.lastY = y; this.down = false;
    } else if (y > this.lastY) {
      if (this.dir !== 1) { this.dir = 1; this.anchorY = this.lastY; }
      if (y - this.anchorY >= THRESHOLD) this.down = true;
    } else if (y < this.lastY) {
      if (this.dir !== -1) { this.dir = -1; this.anchorY = this.lastY; }
      if (this.anchorY - y >= THRESHOLD) this.down = false;
    }
    this.lastY = y;
    this.update();
    /* the tab keeps its place */
    var self = this;
    clearTimeout(this.keepTimer);
    this.keepTimer = setTimeout(function () { self.remember(); }, 300);
  };

  Bar.prototype.update = function () {
    var short = document.documentElement.scrollHeight <= window.innerHeight + 40;
    var hide = this.root.getAttribute('data-hide') === 'true' && phone.matches && this.down && !this.endSeen && !short && !this.root.contains(document.activeElement);
    /* setting "Shrink instead of hiding": a small pill of icons stays (as on iOS 26) */
    var small = hide && this.root.getAttribute('data-shrink') === 'true';
    document.body.classList.toggle('pp-nav-hidden', hide && !small);
    document.body.classList.toggle('pp-nav-small', small);
  };

  /* after a page swapped in place: the tab of the new address, the scroll state, the product marker */
  /* a small spring on the icon of the tab just chosen (CSS .is-pop); restarted on every choice */
  Bar.prototype.pop = function (it) {
    var ic = it && it.querySelector('.pp-icon');
    if (!ic || still.matches) return;
    ic.classList.remove('is-pop');
    void ic.offsetWidth;
    ic.classList.add('is-pop');
    clearTimeout(ic.__ppPop);
    ic.__ppPop = setTimeout(function () { ic.classList.remove('is-pop'); }, 600);
  };

  /* setting "Haptic tap": a tiny tick under the finger (Android; iPhones do not let a page vibrate) */
  Bar.prototype.buzz = function () {
    if (this.root.getAttribute('data-haptics') !== 'true' || !navigator.vibrate) return;
    try { navigator.vibrate(8); } catch (e) {}
  };

  Bar.prototype.sync = function () {
    var was = this.current;
    /* a tab's own page marks that tab; any other page keeps the marks the new page came with (a
       collection is under Shop), copied over by assets/pp-app-nav.js */
    var path = location.pathname.replace(/[/]+$/, '') || '/';
    var own = function (it) { return (it.pathname.replace(/[/]+$/, '') || '/') === path; };
    if (this.dests.some(own)) {
      this.dests.forEach(function (it) {
        if (own(it)) it.setAttribute('aria-current', 'page'); else it.removeAttribute('aria-current');
      });
    }
    this.mark();
    if (this.current && this.current !== was) this.pop(this.current);
    this.remember();
    this.place(false);
    this.lastY = Math.max(0, window.scrollY);
    this.anchorY = this.lastY;
    this.dir = 0;
    this.down = false;
    this.watchAtc();
    this.update();
  };

  /* ---------- each tab's place: its address (with filters) and scroll ---------- */
  Bar.prototype.remember = function () {
    if (!this.current || this.isCart(this.current)) return;
    /* only while the page shown is that tab's own (not while the next one downloads) */
    if ((this.current.pathname.replace(/[/]+$/, '') || '/') !== (location.pathname.replace(/[/]+$/, '') || '/')) return;
    var all = recall('pp-tabs') || {};
    all[this.items.indexOf(this.current)] = { u: location.pathname + location.search, y: Math.round(window.scrollY), t: Date.now() };
    store('pp-tabs', all);
  };

  Bar.prototype.placeOf = function (it) {
    var all = recall('pp-tabs') || {};
    var m = all[this.items.indexOf(it)];
    var base = it.pathname.replace(/[/]+$/, '') || '/';
    if (!m || Date.now() - m.t > MEMORY || (m.u.split('?')[0].replace(/[/]+$/, '') || '/') !== base) return { u: it.href, y: 0 };
    return { u: m.u, y: m.y };
  };

  /* ---------- spring ---------- */
  /* the pill's width for each look (setting "Selection pill"): the glass ones fill the tab, the icon pill
     sits behind the icon, the line is short */
  Bar.prototype.target = function (item) {
    var r = item.getBoundingClientRect();
    var n = this.nav.getBoundingClientRect();
    var pill = this.root.getAttribute('data-pill');
    var w = pill === 'icon' ? Math.min(r.width - 8, 58) : pill === 'dot' ? 20 : Math.min(r.width - (pill === 'liquid' ? 2 : 0), pill === 'liquid' ? 74 : 68);
    return { x: r.left - n.left + (r.width - w) / 2, w: Math.max(0, w) };
  };

  Bar.prototype.draw = function () {
    var s = this.spr;
    var stretch = this.calm ? 0 : Math.min(0.14, Math.abs(s.v) / 5000);
    /* the lift itself (larger, clearer glass) is CSS, so it eases in and out (.is-lifted) */
    this.ind.style.width = s.w + 'px';
    this.ind.style.transform = 'translateX(' + s.x.toFixed(2) + 'px) scale(' + (1 + stretch).toFixed(3) + ',' + (1 - stretch * 0.5).toFixed(3) + ')';
  };

  Bar.prototype.step = function (t) {
    var s = this.spr;
    var self = this;
    var O = 17;        /* natural frequency: a glide, not a snap */
    var Z = this.calm ? 1 : 0.78;   /* below 1: it settles with a small, soft give ("Springy"); 1: none ("Calm") */
    var dt = Math.min(0.032, (t - (s.last || t)) / 1000 || 0.016);
    s.last = t;
    s.v += (-O * O * (s.x - s.target) - 2 * Z * O * s.v) * dt;
    s.x += s.v * dt;
    this.draw();
    if (Math.abs(s.x - s.target) < 0.25 && Math.abs(s.v) < 6 && !s.dragging) {
      s.x = s.target; s.v = 0; s.raf = 0; s.last = 0; this.draw();
      return; /* settled: no more frames */
    }
    s.raf = requestAnimationFrame(function (tt) { self.step(tt); });
  };

  /* the spring, for moves a finger drives (a slide); a CSS glide in progress hands over from where the
     lens is on screen */
  Bar.prototype.glide = function () {
    var self = this;
    if (this.ind.classList.contains('is-gliding')) {
      var m = /matrix\(([^)]+)\)/.exec(getComputedStyle(this.ind).transform || '');
      if (m) this.spr.x = parseFloat(m[1].split(',')[4]) || this.spr.x;
      this.ind.classList.remove('is-gliding');
    }
    if (!this.spr.raf) this.spr.raf = requestAnimationFrame(function (t) { self.step(t); });
  };

  /* a move no finger drives (a tap, letting go, a page arriving): a CSS transition with a small
     overshoot and a brief liquid stretch. The phone's compositor runs it, so it keeps gliding while the
     next page is being built (a script-driven spring stood still for that moment) */
  Bar.prototype.slide = function (x) {
    var s = this.spr;
    if (s.raf) { cancelAnimationFrame(s.raf); s.raf = 0; }
    var from = s.x;
    /* still: no travel; the pill shows at the tab with a short grow (restarted on each move) */
    if (this.motion === 'still') {
      s.x = x; s.v = 0; s.last = 0;
      this.ind.classList.remove('is-gliding', 'is-pop');
      this.draw();
      if (Math.abs(x - from) > 2) { void this.ind.offsetWidth; this.ind.classList.add('is-pop'); }
      return;
    }
    s.x = x; s.v = 0; s.last = 0;
    this.ind.classList.add('is-gliding');
    this.draw();
    var glass = this.ind.firstElementChild;
    if (!this.calm && Math.abs(x - from) > 2 && glass && glass.animate) {
      try {
        glass.animate([{ transform: 'scale(1, 1)' }, { transform: 'scale(1.16, 0.92)', offset: 0.35 }, { transform: 'scale(1, 1)' }], { duration: 440, easing: 'ease-out', composite: 'add' });
      } catch (e) {}
    }
  };

  Bar.prototype.place = function (jump) {
    var at = this.current || this.tab;
    if (!at || !this.ind) { if (this.ind) this.ind.classList.remove('is-on'); return; }
    var t = this.target(at);
    if (!t.w) return;
    this.ind.classList.add('is-on');
    this.spr.target = t.x;
    this.spr.w = t.w;
    if (jump || still.matches) {
      if (this.spr.raf) cancelAnimationFrame(this.spr.raf);
      this.ind.classList.remove('is-gliding');
      this.spr.raf = 0; this.spr.x = t.x; this.spr.v = 0; this.draw();
      return;
    }
    this.slide(t.x);
  };

  /* ---------- hold and slide (pointer events: :active is unreliable on iOS Safari) ---------- */
  Bar.prototype.lift = function (on) {
    this.spr.lifted = on;
    this.ind.classList.toggle('is-lifted', on);
    this.nav.classList.toggle('is-holding', on);
    if (!on) this.under(null);
    this.draw();
  };

  /* the tab under the lens while a finger slides it */
  Bar.prototype.nearest = function () {
    var n = this.nav.getBoundingClientRect();
    var mid = n.left + this.spr.target + this.spr.w / 2;
    var best = null;
    var bd = 1e9;
    this.dests.forEach(function (it) { var r = it.getBoundingClientRect(); var d = Math.abs(r.left + r.width / 2 - mid); if (d < bd) { bd = d; best = it; } });
    return best;
  };

  Bar.prototype.under = function (it) {
    this.dests.forEach(function (x) { x.classList.toggle('is-under', x === it); });
  };

  Bar.prototype.press = function (e) {
    var it = e.target.closest('.ppbb__item');
    if (!it || (e.pointerType === 'mouse' && e.button !== 0)) return;
    var self = this;
    it.classList.add('is-pressed');
    this.pressed = { it: it, x0: e.clientX, y0: e.clientY, id: e.pointerId };
    /* the page behind the tab is made ready while the finger is still down (a tap lasts 60–120 ms) */
    if (this.app && window.PPAppNav && window.PPAppNav.prepare && it.hasAttribute('data-ppbb-dest')) window.PPAppNav.prepare(it.href);
    if (still.matches) return;
    /* a finger resting on a tab (not a quick tap): the lens swells there, ready to slide; a tap never
       moves or swells it before the page answers */
    clearTimeout(this.holdTimer);
    if (it.hasAttribute('data-ppbb-dest')) {
      var mine = it === (this.current || this.tab);
      this.holdTimer = setTimeout(function () {
        if (!self.pressed || self.pressed.it !== it) return;
        self.lift(true);
        if (mine) return;
        self.spr.target = self.target(it).x;
        self.under(it);
        self.glide();
      }, HOLD);
    }
  };

  Bar.prototype.move = function (e) {
    var p = this.pressed;
    var s = this.spr;
    if (!p || e.pointerId !== p.id || still.matches) return;
    var dx = e.clientX - p.x0;
    if (!s.dragging && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(e.clientY - p.y0)) {
      clearTimeout(this.holdTimer);
      s.dragging = true;
      if (!s.lifted) this.lift(true);
      try { this.nav.setPointerCapture(e.pointerId); } catch (x) {}
    }
    if (s.dragging) {
      var n = this.nav.getBoundingClientRect();
      var lo = 4;
      var hi = n.width - s.w - 4;
      var x = e.clientX - n.left - s.w / 2;
      /* past either end it gives a little and pulls back, as on iOS */
      var band = function (d) { return (1 - 1 / (d * 0.55 / 60 + 1)) * 60; };
      s.target = x < lo ? lo - band(lo - x) : x > hi ? hi + band(x - hi) : x;
      this.under(this.nearest());
      this.glide();
    }
  };

  Bar.prototype.up = function () {
    var p = this.pressed;
    var s = this.spr;
    clearTimeout(this.holdTimer);
    if (!p) return;
    p.it.classList.remove('is-pressed');
    this.pressed = null;
    if (s.dragging) {
      s.dragging = false;
      s.dragEnd = Date.now();
      var best = this.nearest();
      this.lift(false);
      if (best && best !== this.current) best.click();
      else this.place(false);
      return;
    }
    this.lift(false);
    /* a hold that ended on the same tab it started from: the click that follows opens it */
    this.place(false);
  };

  /* ---------- items ---------- */
  Bar.prototype.click = function (e) {
    var it = e.target.closest('.ppbb__item');
    if (!it) return;
    /* the browser's own click after a slide: the slide has already chosen */
    if (e.isTrusted && Date.now() - this.spr.dragEnd < 450) { e.preventDefault(); return; }
    if (!it.hasAttribute('data-ppbb-dest')) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    /* the tab you are on, on its own page: back to the top, as in an app (deeper in it, a product under
       Shop: back to the tab's page) */
    if (it === this.current && this.onOwn(it)) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: still.matches ? 'auto' : 'smooth' });
      return;
    }
    if (it.hasAttribute('data-ppbb-shop') && this.root.getAttribute('data-shop-mode') === 'menu' && this.openShopMenu()) {
      e.preventDefault();
      return;
    }
    var at = this.current || this.tab;
    var from = at ? this.items.indexOf(at) : -1;
    var to = this.items.indexOf(it);
    var d = from < 0 ? 'up' : to > from ? 'next' : 'back';
    this.remember();
    /* answer at once: the lens glides to the item */
    this.dests.forEach(function (x) { if (x === it) x.setAttribute('aria-current', 'page'); else x.removeAttribute('aria-current'); });
    this.current = it;
    this.tab = it;
    this.dests.forEach(function (x) { x.classList.remove('is-tab'); });
    this.place(false);
    this.pop(it);
    this.buzz();
    /* in place, without a reload; the Back button then goes to Home, not to the tab before */
    var home = this.home();
    if (this.app && window.PPAppNav.can(it.href)) {
      var pl = this.placeOf(it);
      var hp = home ? this.placeOf(home) : null;
      if (window.PPAppNav.go(pl.u, d, false, pl.y, { tab: true, home: home ? home.href : null, homeY: hp ? hp.y : 0 })) { e.preventDefault(); return; }
    }
    /* the ordinary way: the page eases aside while the next one loads, and glides in; from one tab's page
       to another it takes that page's place in the history, so taps in the bar do not pile up behind Back */
    store('pp-vt', { d: d, from: from, to: to, t: Date.now() });
    document.documentElement.classList.add('pp-leave-' + d);
    if (at && at !== home && this.onOwn(at)) {
      e.preventDefault();
      if (window.PPAppNav && window.PPAppNav.replace) window.PPAppNav.replace(it.href);
      else location.replace(it.href);
    }
  };

  /* the Home item (the one that opens the home page) */
  Bar.prototype.home = function () {
    return this.dests.filter(function (x) { return (x.pathname.replace(/[/]+$/, '') || '/') === '/'; })[0] || null;
  };

  /* the page shown is this tab's own page (not a page inside it) */
  Bar.prototype.onOwn = function (it) {
    return (it.pathname.replace(/[/]+$/, '') || '/') === (location.pathname.replace(/[/]+$/, '') || '/');
  };

  /* Setting "Shop opens: the menu": opens the phone menu, then the menu item named in the setting. */
  Bar.prototype.openShopMenu = function () {
    var summary = document.querySelector('header-drawer summary.header__icon--menu');
    if (!summary) return false;
    var details = summary.parentNode;
    var title = (this.root.getAttribute('data-shop-title') || '').trim().toLowerCase();
    if (!details.classList.contains('menu-opening')) summary.click();
    if (!title) return true;
    var tries = 0;
    (function sub() {
      var target = Array.prototype.filter.call(document.querySelectorAll('#menu-drawer .js-header-drawer-sub > summary'), function (s) {
        return s.textContent.replace(/\s+/g, ' ').trim().toLowerCase() === title;
      })[0];
      if (target && details.classList.contains('menu-opening')) {
        if (!target.parentNode.hasAttribute('open')) target.click();
        return;
      }
      if (++tries < 20) setTimeout(sub, 60);
    })();
    return true;
  };

  /* arrows / Home / End move between items; Enter and Space choose (Space on a link too) */
  Bar.prototype.keys = function (e) {
    var k = this.items.indexOf(document.activeElement);
    if (k < 0) return;
    var n = this.items.length;
    var to = -1;
    if (e.key === 'ArrowRight') to = (k + 1) % n;
    else if (e.key === 'ArrowLeft') to = (k - 1 + n) % n;
    else if (e.key === 'Home') to = 0;
    else if (e.key === 'End') to = n - 1;
    else if (e.key === ' ' && this.items[k].tagName === 'A') { e.preventDefault(); this.items[k].click(); }
    if (to > -1) { e.preventDefault(); this.items[to].focus(); }
  };

  /* ---------- live cart count: the theme announces every cart change ---------- */
  Bar.prototype.subscribeCart = function () {
    var self = this;
    var count = this.root.querySelector('[data-ppbb-count]');
    var sr = this.root.querySelector('[data-ppbb-count-sr]');
    if (!count) return;
    var refresh = function () {
      /* the cart page kept in memory is out of date now */
      if (window.PPAppNav) window.PPAppNav.cartChanged();
      fetch((window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          var n = Number(c.item_count) || 0;
          var was = Number(count.textContent) || 0;
          if (String(n) !== count.textContent && n > 0 && !still.matches) {
            count.classList.remove('is-bump');
            void count.offsetWidth;
            count.classList.add('is-bump');
          }
          /* something went in: it drops into the bag, which takes it with a small squash */
          var item = count.closest('.ppbb__item');
          if (n > was && item && !still.matches) {
            item.classList.remove('is-got');
            void item.offsetWidth;
            item.classList.add('is-got');
            clearTimeout(item.__ppGot);
            item.__ppGot = setTimeout(function () { item.classList.remove('is-got'); }, 900);
            self.buzz();
          }
          count.textContent = n;
          count.hidden = n === 0;
          if (sr) sr.textContent = sr.textContent.replace(/\d+/, n);
        }).catch(function () {});
    };
    this.unsub = [];
    var tries = 0;
    (function wait() {
      /* the theme declares these with const/function at the top level of its scripts, so they are
         reachable by name but not as window properties */
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        ['cartUpdate', 'cartAdd'].forEach(function (ev) {
          if (PUB_SUB_EVENTS[ev]) {
            var u = subscribe(PUB_SUB_EVENTS[ev], refresh);
            if (typeof u === 'function') self.unsub.push(u);
          }
        });
        return;
      }
      if (++tries < 40) setTimeout(wait, 250);
    })();
    this.on(document, 'visibilitychange', function () { if (!document.hidden) refresh(); });
  };

  /* ---------- start, and re-start when the theme editor re-renders the section ---------- */
  function start() {
    document.querySelectorAll('[data-ppbb]').forEach(function (root) {
      if (!root.__ppbb) root.__ppbb = new Bar(root);
    });
  }
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector && e.target.querySelector('[data-ppbb]');
    if (root && root.__ppbb) { root.__ppbb.destroy(); root.__ppbb = null; }
  });
  document.addEventListener('shopify:section:load', start);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
